import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "../assets/css/styles.css";
import farm_logo from "../assets/images/logo2.png";
import useTheme from "../hooks/useTheme";
import { Moon, Sun } from "../components/Icons";

const SECTIONS = [
  { id: "how-it-works", label: "How it works" },
  { id: "about", label: "About" },
  { id: "shares", label: "Shares" },
  { id: "services", label: "Services" },
  { id: "delivery", label: "Delivery" },
  { id: "faq", label: "FAQ" },
  { id: "contact", label: "Contact" },
];

const Brand = () => (
  <span className="brand">
    <span className="brand-mark">
      <img src={farm_logo} alt="" />
    </span>
    <span className="brand-text">
      <span className="brand-name">Farmer Boaz</span>
      <span className="brand-tag">Farm to table</span>
    </span>
  </span>
);

const Navbar = () => {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the drawer on navigation and lock the page behind it while open.
  useEffect(() => setMenuOpen(false), [location.pathname, location.hash]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    const onKeyDown = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  // On the home page anchor links scroll in place; elsewhere they navigate home first.
  const goToSection = useCallback(
    (id) => (event) => {
      setMenuOpen(false);
      if (!isHome) return;
      const target = document.getElementById(id);
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: "smooth" });
      window.history.replaceState(null, "", `/#${id}`);
    },
    [isHome]
  );

  return (
    <>
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      <header
        className="site-header"
        data-scrolled={scrolled ? "true" : "false"}
        data-solid={isHome ? "false" : "true"}
      >
        <nav className="nav-shell" aria-label="Main">
          <Link to="/" aria-label="Farmer Boaz — home">
            <Brand />
          </Link>

          <ul className="nav-links">
            {SECTIONS.map((section) => (
              <li key={section.id}>
                <a
                  className="nav-link"
                  href={`/#${section.id}`}
                  onClick={goToSection(section.id)}
                >
                  {section.label}
                </a>
              </li>
            ))}
          </ul>

          <div className="nav-actions">
            <button
              type="button"
              className="theme-toggle"
              onClick={toggleTheme}
              aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
              title={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
            >
              {theme === "dark" ? <Sun /> : <Moon />}
            </button>

            <Link to="/order-now" className="btn nav-cta">
              Order Now
            </Link>

            <button
              type="button"
              className="nav-toggle"
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </nav>
      </header>

      <div
        className={`nav-backdrop ${menuOpen ? "is-open" : ""}`}
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
      />

      <div
        id="mobile-menu"
        className={`nav-drawer ${menuOpen ? "is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Site menu"
        aria-hidden={!menuOpen}
        {...(menuOpen ? {} : { inert: "" })}
      >
        <div className="drawer-head">
          <Brand />
          <button
            type="button"
            className="drawer-close"
            onClick={() => setMenuOpen(false)}
            aria-label="Close menu"
          >
            &#10005;
          </button>
        </div>

        {SECTIONS.map((section) => (
          <a
            key={section.id}
            className="drawer-link"
            href={`/#${section.id}`}
            onClick={goToSection(section.id)}
          >
            {section.label}
            <span aria-hidden="true">&rarr;</span>
          </a>
        ))}

        <Link to="/order-now" className="btn btn--block drawer-cta">
          Order Now
        </Link>
        <Link to="/track-order" className="btn btn--ghost btn--block" style={{ marginTop: "0.6rem" }}>
          Track Order
        </Link>
      </div>
    </>
  );
};

export default Navbar;
